/* eslint-disable react/no-unescaped-entities, react-hooks/immutability -- legacy onboarding: texte FR + règle expérimentale, fonctionnel et validé en prod */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
    Building2, FileText, CreditCard, CheckCircle, Upload, 
    ArrowRight, ArrowLeft, Loader2, AlertCircle, Shield,
    FileCheck, Euro, Calendar
} from 'lucide-react';

/**
 * AgencyOnboardingPage - Complete agency setup after account creation
 * Steps: 1. Documents, 2. Payment, 3. Confirmation
 */
export default function AgencyOnboardingPage() {
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();
    const [searchParams] = useSearchParams();
    
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [agency, setAgency] = useState(null);
    const [uploadProgress, setUploadProgress] = useState({});
    
    // Document upload state
    const [documents, setDocuments] = useState({
        kbis: null,
        carte_pro: null,
        id_card: null
    });
    
    // Billing cycle
    const [billingCycle, setBillingCycle] = useState('monthly');
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    
    useEffect(() => {
        loadAgencyData();
    }, []);
    
    useEffect(() => {
        if (sessionId) {
            // Returning from Stripe - poll payment status
            pollPaymentStatus(sessionId);
        }
    }, [sessionId]);
    
    const loadAgencyData = async () => {
        try {
            const response = await api.get('/agencies/me');
            setAgency(response.data);
            
            // Determine starting step based on agency status
            if (response.data?.documents?.verified) {
                setStep(2); // Skip to payment
            }
            if (response.data?.status === 'active') {
                setStep(3); // Already complete
            }
        } catch (error) {
            console.error('Error loading agency:', error);
        }
    };
    
    const handleFileChange = (docType, file) => {
        if (file) {
            // Validate file type
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Format non supporté. Utilisez PDF, JPEG ou PNG.');
                return;
            }
            
            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                toast.error('Fichier trop volumineux. Maximum 10 Mo.');
                return;
            }
            
            setDocuments(prev => ({ ...prev, [docType]: file }));
        }
    };
    
    const uploadDocument = async (docType, file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', docType);
        
        try {
            setUploadProgress(prev => ({ ...prev, [docType]: 'uploading' }));
            
            const response = await api.post('/users/me/agency/documents/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setUploadProgress(prev => ({ ...prev, [docType]: 'done' }));
            return response.data;
        } catch (error) {
            setUploadProgress(prev => ({ ...prev, [docType]: 'error' }));
            throw error;
        }
    };
    
    const handleSubmitDocuments = async () => {
        // Validate all required documents
        if (!documents.kbis || !documents.id_card) {
            toast.error('Veuillez fournir tous les documents obligatoires');
            return;
        }
        
        setLoading(true);
        
        try {
            // Upload all documents
            const uploadPromises = [];
            
            if (documents.kbis) {
                uploadPromises.push(uploadDocument('kbis', documents.kbis));
            }
            if (documents.carte_pro) {
                uploadPromises.push(uploadDocument('carte_pro', documents.carte_pro));
            }
            if (documents.id_card) {
                uploadPromises.push(uploadDocument('id_card', documents.id_card));
            }
            
            await Promise.all(uploadPromises);
            
            toast.success('Documents téléversés avec succès !');
            setStep(2);
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Erreur lors du téléversement des documents');
        } finally {
            setLoading(false);
        }
    };
    
    const handleStartPayment = async () => {
        setLoading(true);
        
        try {
            const origin = window.location.origin;
            
            const response = await api.post('/payments/agency/checkout', {
                billing_cycle: billingCycle,
                origin_url: origin
            });
            
            // Redirect to Stripe checkout
            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error('Payment error:', error);
            toast.error('Erreur lors de la création de la session de paiement');
        } finally {
            setLoading(false);
        }
    };
    
    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 10;
        const pollInterval = 2000;
        
        if (attempts >= maxAttempts) {
            toast.error('Vérification du paiement expirée. Contactez le support.');
            return;
        }
        
        try {
            const response = await api.get(`/payments/checkout/status/${sessionId}`);
            const { payment_status, status } = response.data;
            
            if (payment_status === 'paid') {
                toast.success('Paiement réussi ! Votre compte est maintenant actif.');
                setStep(3);
                if (refreshUser) refreshUser();
                return;
            } else if (status === 'expired') {
                toast.error('Session de paiement expirée. Veuillez réessayer.');
                return;
            }
            
            // Continue polling
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        } catch (error) {
            console.error('Payment status error:', error);
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        }
    };
    
    const handleFinish = () => {
        navigate('/admin', { replace: true });
    };
    
    // Calculate pricing
    const monthlyPrice = 79;
    const agentMonthlyPrice = 19; // Option Agent Premium (réseau), payée par l'agent
    const annualDiscount = 0.10;
    const annualBonus = 20; // Bonus signatures
    const fmtEur = (n) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const getPrice = () => {
        if (billingCycle === 'annual') {
            return {
                agency: fmtEur(monthlyPrice * 12 * (1 - annualDiscount)),
                agent: fmtEur(agentMonthlyPrice * 12 * (1 - annualDiscount)),
                period: 'an',
                bonus: annualBonus
            };
        }
        return {
            agency: fmtEur(monthlyPrice),
            agent: fmtEur(agentMonthlyPrice),
            period: 'mois',
            bonus: 0
        };
    };
    
    const pricing = getPrice();
    
    const steps = [
        { id: 1, title: 'Documents', icon: FileText },
        { id: 2, title: 'Paiement', icon: CreditCard },
        { id: 3, title: 'Confirmation', icon: CheckCircle }
    ];
    
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
            {/* Header */}
            <div className="bg-white border-b">
                <div className="max-w-4xl mx-auto px-4 py-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#681793] to-[#FF914D] rounded-xl flex items-center justify-center">
                            <span className="text-white font-bold">H</span>
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-900">Configuration de votre agence</h1>
                            <p className="text-sm text-slate-500">{agency?.name || 'Chargement...'}</p>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Stepper */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="flex items-center justify-between mb-8">
                    {steps.map((s, idx) => (
                        <React.Fragment key={s.id}>
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                                    step >= s.id 
                                        ? 'bg-gradient-to-br from-[#681793] to-[#FF914D] text-white' 
                                        : 'bg-slate-200 text-slate-500'
                                }`}>
                                    {step > s.id ? (
                                        <CheckCircle className="w-5 h-5" />
                                    ) : (
                                        <s.icon className="w-5 h-5" />
                                    )}
                                </div>
                                <span className={`hidden sm:block font-medium ${
                                    step >= s.id ? 'text-slate-900' : 'text-slate-400'
                                }`}>
                                    {s.title}
                                </span>
                            </div>
                            {idx < steps.length - 1 && (
                                <div className={`flex-1 h-1 mx-4 rounded-full ${
                                    step > s.id ? 'bg-gradient-to-r from-[#681793] to-[#FF914D]' : 'bg-slate-200'
                                }`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
                
                {/* Step Content */}
                <Card className="shadow-xl">
                    {/* Step 1: Documents */}
                    {step === 1 && (
                        <>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-[#681793]" />
                                    Documents justificatifs
                                </CardTitle>
                                <CardDescription>
                                    Téléversez les documents nécessaires à l'activation de votre compte
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* KBIS */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        Extrait KBIS <span className="text-red-500">*</span>
                                        <span className="text-xs text-slate-400">(moins de 3 mois)</span>
                                    </Label>
                                    <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                                        documents.kbis 
                                            ? 'border-green-300 bg-green-50' 
                                            : 'border-slate-200 hover:border-[#681793]/50'
                                    }`}>
                                        <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={(e) => handleFileChange('kbis', e.target.files[0])}
                                            className="hidden"
                                            id="kbis-upload"
                                            data-testid="kbis-upload"
                                        />
                                        <label htmlFor="kbis-upload" className="cursor-pointer">
                                            {documents.kbis ? (
                                                <div className="flex items-center justify-center gap-2 text-green-600">
                                                    <FileCheck className="w-5 h-5" />
                                                    <span className="font-medium">{documents.kbis.name}</span>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <Upload className="w-8 h-8 mx-auto text-slate-400" />
                                                    <p className="text-sm text-slate-500">Cliquez pour téléverser</p>
                                                    <p className="text-xs text-slate-400">PDF, JPEG ou PNG (max 10 Mo)</p>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                </div>
                                
                                {/* Carte Pro (optionnel) */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        Carte professionnelle
                                        <span className="text-xs text-slate-400">(optionnel)</span>
                                    </Label>
                                    <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                                        documents.carte_pro 
                                            ? 'border-green-300 bg-green-50' 
                                            : 'border-slate-200 hover:border-[#681793]/50'
                                    }`}>
                                        <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={(e) => handleFileChange('carte_pro', e.target.files[0])}
                                            className="hidden"
                                            id="carte-pro-upload"
                                            data-testid="carte-pro-upload"
                                        />
                                        <label htmlFor="carte-pro-upload" className="cursor-pointer">
                                            {documents.carte_pro ? (
                                                <div className="flex items-center justify-center gap-2 text-green-600">
                                                    <FileCheck className="w-5 h-5" />
                                                    <span className="font-medium">{documents.carte_pro.name}</span>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <Upload className="w-8 h-8 mx-auto text-slate-400" />
                                                    <p className="text-sm text-slate-500">Cliquez pour téléverser</p>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                </div>
                                
                                {/* ID Card */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        Pièce d'identité du gérant <span className="text-red-500">*</span>
                                    </Label>
                                    <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                                        documents.id_card 
                                            ? 'border-green-300 bg-green-50' 
                                            : 'border-slate-200 hover:border-[#681793]/50'
                                    }`}>
                                        <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={(e) => handleFileChange('id_card', e.target.files[0])}
                                            className="hidden"
                                            id="id-card-upload"
                                            data-testid="id-card-upload"
                                        />
                                        <label htmlFor="id-card-upload" className="cursor-pointer">
                                            {documents.id_card ? (
                                                <div className="flex items-center justify-center gap-2 text-green-600">
                                                    <FileCheck className="w-5 h-5" />
                                                    <span className="font-medium">{documents.id_card.name}</span>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <Upload className="w-8 h-8 mx-auto text-slate-400" />
                                                    <p className="text-sm text-slate-500">Cliquez pour téléverser</p>
                                                    <p className="text-xs text-slate-400">CNI, passeport ou titre de séjour</p>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                </div>
                                
                                {/* Security note */}
                                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <Shield className="w-5 h-5 text-[#681793] shrink-0 mt-0.5" />
                                    <div className="text-sm text-slate-600">
                                        <p className="font-medium text-slate-900 mb-1">Vos données sont protégées</p>
                                        <p>Vos documents sont stockés de manière sécurisée et ne sont utilisés que pour vérifier votre identité conformément à la réglementation.</p>
                                    </div>
                                </div>
                                
                                <div className="flex justify-end pt-4">
                                    <Button
                                        onClick={handleSubmitDocuments}
                                        disabled={loading || !documents.kbis || !documents.id_card}
                                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white px-8"
                                        data-testid="submit-documents-btn"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Téléversement...
                                            </>
                                        ) : (
                                            <>
                                                Continuer
                                                <ArrowRight className="w-4 h-4 ml-2" />
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </CardContent>
                        </>
                    )}
                    
                    {/* Step 2: Payment */}
                    {step === 2 && (
                        <>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <CreditCard className="w-5 h-5 text-[#681793]" />
                                    Configuration du paiement
                                </CardTitle>
                                <CardDescription>
                                    Choisissez votre formule et configurez votre moyen de paiement
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Billing cycle selector */}
                                <div className="space-y-3">
                                    <Label>Fréquence de facturation</Label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button
                                            onClick={() => setBillingCycle('monthly')}
                                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                                                billingCycle === 'monthly'
                                                    ? 'border-[#681793] bg-[#681793]/5'
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            data-testid="billing-monthly"
                                        >
                                            <div className="flex items-center gap-2 mb-2">
                                                <Calendar className="w-4 h-4 text-[#681793]" />
                                                <span className="font-bold text-slate-900">Mensuel</span>
                                            </div>
                                            <p className="text-2xl font-black text-slate-900">
                                                {fmtEur(monthlyPrice)}€ <span className="text-sm font-normal text-slate-500">HT/mois</span>
                                            </p>
                                            <p className="text-xs text-slate-500 mt-1">Sans engagement</p>
                                        </button>
                                        
                                        <button
                                            onClick={() => setBillingCycle('annual')}
                                            className={`p-4 rounded-xl border-2 text-left transition-all relative ${
                                                billingCycle === 'annual'
                                                    ? 'border-[#681793] bg-[#681793]/5'
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            data-testid="billing-annual"
                                        >
                                            <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">
                                                -10%
                                            </div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <Calendar className="w-4 h-4 text-[#681793]" />
                                                <span className="font-bold text-slate-900">Annuel</span>
                                            </div>
                                            <p className="text-2xl font-black text-slate-900">
                                                {fmtEur(monthlyPrice * 12 * (1 - annualDiscount))}€ <span className="text-sm font-normal text-slate-500">HT/an</span>
                                            </p>
                                            <p className="text-xs text-green-600 font-medium mt-1">
                                                +{annualBonus} signatures bonus
                                            </p>
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Annual benefits */}
                                {billingCycle === 'annual' && (
                                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                                        <p className="text-sm font-medium text-green-800 mb-2">Avantages du paiement annuel :</p>
                                        <ul className="space-y-1 text-sm text-green-700">
                                            <li className="flex items-center gap-2">
                                                <CheckCircle className="w-4 h-4" />
                                                10% de réduction sur l'abonnement
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <CheckCircle className="w-4 h-4" />
                                                Signatures accordées d'avance
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <CheckCircle className="w-4 h-4" />
                                                +{annualBonus} signatures bonus offertes
                                            </li>
                                        </ul>
                                    </div>
                                )}
                                
                                {/* Summary */}
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <p className="text-sm font-medium text-slate-700 mb-3">Récapitulatif</p>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Abonnement Agence (agents illimités inclus)</span>
                                            <span className="font-medium">{pricing.agency}€ HT/{pricing.period}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Option Agent Premium — réseau (payée par l'agent)</span>
                                            <span className="font-medium">{pricing.agent}€ HT/{pricing.period}/agent</span>
                                        </div>
                                        <hr className="my-2 border-slate-200" />
                                        <div className="flex justify-between text-base">
                                            <span className="font-medium text-slate-900">Total aujourd'hui</span>
                                            <span className="font-bold text-[#681793]">{pricing.agency}€ HT</span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Payment methods info */}
                                <div className="flex items-center gap-4 text-xs text-slate-500">
                                    <span>Paiement sécurisé par</span>
                                    <div className="flex items-center gap-2">
                                        <div className="px-2 py-1 bg-slate-100 rounded font-medium">VISA</div>
                                        <div className="px-2 py-1 bg-slate-100 rounded font-medium">Mastercard</div>
                                        <div className="px-2 py-1 bg-slate-100 rounded font-medium">SEPA</div>
                                    </div>
                                </div>
                                
                                <div className="flex justify-between pt-4">
                                    <Button
                                        variant="outline"
                                        onClick={() => setStep(1)}
                                        data-testid="back-to-documents"
                                    >
                                        <ArrowLeft className="w-4 h-4 mr-2" />
                                        Retour
                                    </Button>
                                    <Button
                                        onClick={handleStartPayment}
                                        disabled={loading}
                                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white px-8"
                                        data-testid="start-payment-btn"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Chargement...
                                            </>
                                        ) : (
                                            <>
                                                <CreditCard className="w-4 h-4 mr-2" />
                                                Payer {pricing.agency}€ HT
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </CardContent>
                        </>
                    )}
                    
                    {/* Step 3: Confirmation */}
                    {step === 3 && (
                        <>
                            <CardHeader className="text-center pb-2">
                                <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                                    <CheckCircle className="w-10 h-10 text-green-600" />
                                </div>
                                <CardTitle className="text-2xl text-green-700">
                                    Félicitations !
                                </CardTitle>
                                <CardDescription className="text-base">
                                    Votre compte Homly est maintenant actif
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-3">
                                    <p className="font-medium text-slate-900">Prochaines étapes :</p>
                                    <ul className="space-y-2 text-sm text-slate-600">
                                        <li className="flex items-start gap-3">
                                            <div className="w-6 h-6 rounded-full bg-[#681793]/10 flex items-center justify-center shrink-0 mt-0.5">
                                                <span className="text-xs font-bold text-[#681793]">1</span>
                                            </div>
                                            <span>Invitez vos agents depuis l'onglet "Équipe"</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <div className="w-6 h-6 rounded-full bg-[#681793]/10 flex items-center justify-center shrink-0 mt-0.5">
                                                <span className="text-xs font-bold text-[#681793]">2</span>
                                            </div>
                                            <span>Créez votre premier mandat</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <div className="w-6 h-6 rounded-full bg-[#681793]/10 flex items-center justify-center shrink-0 mt-0.5">
                                                <span className="text-xs font-bold text-[#681793]">3</span>
                                            </div>
                                            <span>Personnalisez les paramètres de votre agence</span>
                                        </li>
                                    </ul>
                                </div>
                                
                                <div className="text-center pt-4">
                                    <Button
                                        onClick={handleFinish}
                                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white px-10 h-12"
                                        data-testid="go-to-dashboard-btn"
                                    >
                                        Accéder à mon dashboard
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
}
